import {
  compareCategories,
  comparePlatforms,
  getPlatform,
} from "@/v2-backoffice-client/uptime/uptime.utils"
import {
  UPTIME_PLATFORM_ORDER,
  UPTIME_PLATFORM_PLACEPOINT_V2,
  type UptimePlatform,
} from "@/v2-backoffice-client/uptime/uptime.constants"

// Minimal structural type accepted by every function in this module.
// Both UptimeTarget (full data) and UptimeInlineFragment
// (inline, no history arrays) satisfy this shape, so callers using
// either fragment type can pass targets without a cast.
type UptimeTarget = {
  Source: string
  Identifier: string
  IsMuted: boolean
  LatestStatus: string
  IsSlowOutlier: boolean
  HasMetadataDrift: boolean
  HasCountDrift: boolean
  TotalChecks90d: number
  DownChecks90d: number
  AvgDurationMs90d?: number | null
}

// Single source of truth for "how do we bucket / count / summarise a
// list of uptime targets?". Both top-level tables (incidents,
// per-category) and the sidebar / status-breakdown bar all need the
// same answers; without a shared util each one runs its own
// bucketing pass on every poll cycle, which on a 250-target fleet
// that polls every 60s adds up to a measurable amount of wasted
// work and a lot of duplicated logic in the components.
//
// The bucket vocabulary is "all / down / slow / drift / muted",
// matching the segmented status pills the operator interacts with.
// `slow` and `drift` are flags orthogonal to LatestStatus, so the
// rules below pin a single bucket per target so the segments add
// up cleanly:
//
//   muted  → operator silenced it; takes precedence over everything
//            (the wall-display and KPIs all respect mute).
//   down   → LatestStatus === "down".
//   slow   → not down + IsSlowOutlier.
//   drift  → not down/slow/muted + (HasMetadataDrift || HasCountDrift).
//   all    → everything except muted.
//
// `drift` is mutually exclusive with `down`/`slow` here so a single
// row never lands in two segmented-pill counts; consumers that need
// "down rows that are also drifting" should query the raw flags on
// the row itself.
//
// Both metadata drift (e.g. WMS layer-name change, ArcGIS field-set
// change) and feature-count drift (e.g. Geodata reclassifies
// "hagesenter" as "blomsterbutikk" so the count drops 90%) collapse
// into the same `drift` bucket here. Operators looking for a
// specific kind use the per-row flags or the count column.

export type UptimeBucket = "all" | "down" | "slow" | "drift" | "muted"

export type UptimeBucketCounts = Readonly<{
  all: number
  down: number
  slow: number
  drift: number
  muted: number
}>

// Predicate used by both incident and per-category tables to filter
// the visible row list once the operator picks a status pill. Kept
// in lockstep with `countBuckets` below: every row that increments
// counts.X must also satisfy `matchesBucket(_, X)` and vice versa.
// Drift here surfaces *every* drifting row (down/slow included)
// because the "Drift" pill on the table is a "show me everything
// that is drifting" view, not a leftover bucket -- the count
// matches.
export function matchesUptimeBucket(
  t: UptimeTarget,
  bucket: UptimeBucket,
): boolean {
  if (bucket === "muted") return t.IsMuted
  if (t.IsMuted) return false
  if (bucket === "all") return true
  switch (bucket) {
    case "down":
      return t.LatestStatus === "down"
    case "slow":
      return t.LatestStatus !== "down" && t.IsSlowOutlier
    case "drift":
      return t.HasMetadataDrift || t.HasCountDrift
  }
}

// hasAnyDrift collapses metadata + count drift into the single
// "is this row drifting?" predicate the bucket logic, the column
// renderer, and the dashboard split all share. Centralised so the
// three call sites can never disagree on the answer.
export function hasAnyDrift(t: UptimeTarget): boolean {
  return t.HasMetadataDrift || t.HasCountDrift
}

// Single-pass count of every bucket the segmented pills can show.
// Replaces three separate filter-and-length passes (down, slow,
// drift) plus the muted scan; the per-bucket behaviour matches
// `matchesUptimeBucket` so the badge counts and the table contents
// always agree. `all` is "every non-muted row" so the headline
// "All N" pill matches what the table renders by default.
export function countUptimeBuckets(
  targets: ReadonlyArray<UptimeTarget>,
): UptimeBucketCounts {
  let down = 0
  let slow = 0
  let drift = 0
  let muted = 0
  for (const t of targets) {
    if (t.IsMuted) {
      muted++
      continue
    }
    if (t.LatestStatus === "down") down++
    if (t.LatestStatus !== "down" && t.IsSlowOutlier) slow++
    if (hasAnyDrift(t)) drift++
  }
  return {
    all: targets.length - muted,
    down,
    slow,
    drift,
    muted,
  }
}

// Per-category aggregate stats. Single-pass replacement for the two
// near-duplicated `summariseGroup` implementations that lived in
// UptimeCategoryNav and UptimeCategoryTable; the new shape is the
// union of both so callers can render either summary line from the
// same data. Callers that only need a couple of fields just ignore
// the rest.
//
// Mute semantics: `up` / `down` / `skipped` / `slow` / `drift` /
// `total` / `medianLatencyMs` / `aggregateUptimePercent` describe
// the **active** (non-muted) slice of the group only. `muted` is
// the parallel informational counter ("3 are silenced") and never
// participates in any of the other fields. This means
// `up + down + skipped === total`, and the sidebar/category-table
// headers can render "5/8 up · 3 down · +2 muted" without any of
// the math feeling off.
export type UptimeGroupStats = Readonly<{
  up: number
  down: number
  skipped: number
  slow: number
  drift: number
  muted: number
  total: number
  // Median of per-target AvgDurationMs90d across rows that have a
  // measurable baseline. Captures "how slow is this group on a
  // typical day" without letting one outlier dominate the readout.
  medianLatencyMs: number | null
  // Pooled 90-day uptime: (sum(checks - downChecks) / sum(checks))
  // so a category with one chatty target and one quiet one gets
  // weighted by probe count rather than by row count.
  aggregateUptimePercent: number | null
}>

export function summariseUptimeGroup(
  targets: ReadonlyArray<UptimeTarget>,
): UptimeGroupStats {
  let up = 0
  let down = 0
  let skipped = 0
  let slow = 0
  let drift = 0
  let muted = 0
  let active = 0
  let totalChecks = 0
  let downChecks = 0
  // Collect baselines for the median in a flat array; sorting at
  // the end is cheaper than maintaining a sorted structure for the
  // tens-of-targets-per-category we typically see.
  const latencies: number[] = []

  for (const t of targets) {
    // Muted rows count toward the informational `muted` field only;
    // every other counter and the latency / uptime aggregations skip
    // them so the section headers reflect the active slice cleanly.
    if (t.IsMuted) {
      muted++
      continue
    }
    active++
    if (t.LatestStatus === "up") up++
    else if (t.LatestStatus === "down") down++
    else skipped++
    if (t.IsSlowOutlier) slow++
    if (hasAnyDrift(t)) drift++
    totalChecks += t.TotalChecks90d
    downChecks += t.DownChecks90d
    const l = t.AvgDurationMs90d ?? null
    if (l != null && l > 0) latencies.push(l)
  }

  let medianLatencyMs: number | null = null
  if (latencies.length > 0) {
    latencies.sort((a, b) => a - b)
    const mid = Math.floor(latencies.length / 2)
    medianLatencyMs =
      latencies.length % 2 === 0
        ? Math.round((latencies[mid - 1] + latencies[mid]) / 2)
        : latencies[mid]
  }

  const aggregateUptimePercent =
    totalChecks > 0 ? (100 * (totalChecks - downChecks)) / totalChecks : null

  return {
    up,
    down,
    skipped,
    slow,
    drift,
    muted,
    total: active,
    medianLatencyMs,
    aggregateUptimePercent,
  }
}

// Category bucket: one entry in the sidebar's per-platform list.
// The `groupKey` is the (platform, source) compound used by the
// board to identify the active selection -- needed because
// `app.release` is shared between Placepoint v1 and v2, and a flat
// source-only key would collapse them.
export type UptimeCategoryGroup = Readonly<{
  groupKey: string
  source: string
  platform: UptimePlatform
  targets: UptimeTarget[]
  stats: UptimeGroupStats
}>

// Platform bucket: the parent header in the sidebar plus its
// pre-summarised stats so the platform pill (eg. "32/45 up") doesn't
// re-walk the category list.
export type UptimePlatformGroup = Readonly<{
  platform: UptimePlatform
  groups: UptimeCategoryGroup[]
  stats: UptimeGroupStats
}>

// (platform, source) compound key. Used as the sidebar's selection
// id so the right pane can re-show the exact bucket the operator
// clicked. Pairs with `parseGroupKey` for round-tripping.
export function makeGroupKey(platform: UptimePlatform, source: string): string {
  return `${platform}|${source}`
}

// Inverse of `makeGroupKey`. Returns the parts so the right pane
// can resolve a sidebar selection back into "this source, filtered
// to this platform". Returns null for keys that don't follow the
// compound format -- callers should fall back gracefully.
export function parseGroupKey(
  key: string,
): { platform: UptimePlatform; source: string } | null {
  const sep = key.indexOf("|")
  if (sep < 0) return null
  const platform = key.slice(0, sep) as UptimePlatform
  if (!(UPTIME_PLATFORM_ORDER as readonly string[]).includes(platform)) {
    return null
  }
  return { platform, source: key.slice(sep + 1) }
}

// Fully digested view of a target list, computed in a single pass
// so the parent of every table/sidebar/breakdown only walks the
// fleet once per poll cycle. Returned shape:
//
//   - `byPlatform`: ordered list of platform headers + their child
//     categories, each pre-summarised. Replaces the old flat
//     `bySource` so the sidebar can render the platform-grouped
//     layout the operator now expects.
//   - `groupByKey`: lookup from a (platform, source) compound key
//     to its category group, used by the board to pluck the
//     selected slice for the right pane without re-filtering the
//     fleet.
//   - `attentionCounts`: down / slow / drift across the active
//     fleet (muted rows excluded). Drives the "Active incidents"
//     pseudo-item in the sidebar.
//   - `bucketCounts`: identical to `countUptimeBuckets(targets)`
//     but reusing the same loop.
//
// Every counter above already drops muted rows -- the per-fleet
// aggregation helpers in `uptime.charts.utils.ts` and the per-group
// summariser do the same -- so callers can pass the raw fleet
// through to them without pre-filtering.
export type UptimeBoardData = Readonly<{
  byPlatform: ReadonlyArray<UptimePlatformGroup>
  groupByKey: ReadonlyMap<string, UptimeCategoryGroup>
  attentionCounts: Readonly<{ down: number; slow: number; drift: number }>
  bucketCounts: UptimeBucketCounts
}>

export function buildUptimeBoardData(
  targets: ReadonlyArray<UptimeTarget>,
): UptimeBoardData {
  // First pass: split the fleet into per-(platform, source) buckets
  // and count the post-mute "attention" set in the same loop. We
  // also do the bucket counts (countUptimeBuckets-equivalent) here
  // so a 250-target fleet incurs exactly one loop.
  const byKeyMap = new Map<
    string,
    { platform: UptimePlatform; source: string; targets: UptimeTarget[] }
  >()
  const byPlatformMap = new Map<UptimePlatform, UptimeTarget[]>()
  let attentionDown = 0
  let attentionSlow = 0
  let attentionDrift = 0
  let bucketDown = 0
  let bucketSlow = 0
  let bucketDrift = 0
  let bucketMuted = 0

  for (const t of targets) {
    const platform = getPlatform(t) ?? UPTIME_PLATFORM_PLACEPOINT_V2
    const key = makeGroupKey(platform, t.Source)
    let entry = byKeyMap.get(key)
    if (!entry) {
      entry = { platform, source: t.Source, targets: [] }
      byKeyMap.set(key, entry)
    }
    entry.targets.push(t)

    let platformTargets = byPlatformMap.get(platform)
    if (!platformTargets) {
      platformTargets = []
      byPlatformMap.set(platform, platformTargets)
    }
    platformTargets.push(t)

    if (t.IsMuted) {
      bucketMuted++
      continue
    }
    if (t.LatestStatus === "down") {
      attentionDown++
      bucketDown++
    } else if (t.IsSlowOutlier) {
      attentionSlow++
      bucketSlow++
    }
    if (hasAnyDrift(t)) {
      attentionDrift++
      bucketDrift++
    }
  }

  // Build the per-key category groups (with stats), keyed by
  // groupKey for downstream lookup. `summariseUptimeGroup` is
  // O(group size), so summed this stays O(N) over the fleet.
  const groupByKey = new Map<string, UptimeCategoryGroup>()
  for (const [groupKey, entry] of byKeyMap) {
    groupByKey.set(groupKey, {
      groupKey,
      source: entry.source,
      platform: entry.platform,
      targets: entry.targets,
      stats: summariseUptimeGroup(entry.targets),
    })
  }

  // Fold the category groups under their platform header, ordered
  // by canonical platform order on the outside and canonical
  // category order within each platform.
  const byPlatform: UptimePlatformGroup[] = []
  const seenPlatforms = new Set<UptimePlatform>()
  for (const entry of groupByKey.values()) {
    seenPlatforms.add(entry.platform)
  }
  const orderedPlatforms = Array.from(seenPlatforms).toSorted((a, b) =>
    comparePlatforms(a, b),
  )
  for (const platform of orderedPlatforms) {
    const groups: UptimeCategoryGroup[] = []
    for (const entry of groupByKey.values()) {
      if (entry.platform === platform) groups.push(entry)
    }
    groups.sort((a, b) => compareCategories(a.source, b.source))
    const platformTargets = byPlatformMap.get(platform) ?? []
    byPlatform.push({
      platform,
      groups,
      stats: summariseUptimeGroup(platformTargets),
    })
  }

  return {
    byPlatform,
    groupByKey,
    attentionCounts: {
      down: attentionDown,
      slow: attentionSlow,
      drift: attentionDrift,
    },
    bucketCounts: {
      all: targets.length - bucketMuted,
      down: bucketDown,
      slow: bucketSlow,
      drift: bucketDrift,
      muted: bucketMuted,
    },
  }
}
